import ImaScriptLoader from 'app/components/tv/handler/ImaScriptLoader';
import VideoAds, {SetAdPlaying, SetRemainingTime} from 'app/components/tv/handler/VideoAds';
import VideoAdsException from 'app/exceptions/VideoAdsException';
import {captureSentryExceptionAndThrowLocal} from 'app/services/sentry/sentryLogging';
import {VideoJsPlayer} from 'video.js';

export default class VideoAdController {
  private static instance: VideoAdController | null;
  preventAds: boolean;
  private readonly getVideoAds: (player: VideoJsPlayer) => VideoAds;
  private readonly shouldPlayAdFn: () => boolean;
  private videoAds: VideoAds | null;
  private readonly imaScriptLoader: ImaScriptLoader;

  private constructor(
    adsDisabled: boolean,
    getVideoAds: (player: VideoJsPlayer) => VideoAds,
    shouldPlayAdFn: () => boolean
  ) {
    this.preventAds = adsDisabled;
    this.getVideoAds = getVideoAds;
    this.shouldPlayAdFn = shouldPlayAdFn;
    this.imaScriptLoader = new ImaScriptLoader();
  }

  static getInstance(
    adsDisabled?: boolean,
    getVideoAds?: (player: VideoJsPlayer) => VideoAds,
    shouldPlayAdFn?: () => boolean
  ): VideoAdController {
    if (!VideoAdController.instance) {
      if (adsDisabled === undefined || getVideoAds === undefined || shouldPlayAdFn === undefined) {
        throw new VideoAdsException(
          'Parameters adsDisabled, newVideoAds and shouldPlayAdFn are required to create a VideoAdController'
        );
      }
      VideoAdController.instance = new VideoAdController(adsDisabled, getVideoAds, shouldPlayAdFn);
    }
    return VideoAdController.instance;
  }

  async loadImaScript(): Promise<void> {
    if (this.preventAds) {
      return;
    }
    try {
      await this.imaScriptLoader.load();
    } catch (e) {
      // log to sentry if we can't load the ima-script, ads can not be played'
      // Todo: check if this should be added to GA Event 'adserver_never_starts_bidding'
      captureSentryExceptionAndThrowLocal(e);
      this.preventAds = true;
    }
  }

  async setupAds(
    player: VideoJsPlayer,
    setAdPlaying: SetAdPlaying,
    setRemainingTime: SetRemainingTime,
    isFirstPage: boolean,
    ppid: string
  ): Promise<void> {
    if (this.preventAds) {
      return;
    }
    try {
      this.videoAds = this.getVideoAds(player);
      await this.videoAds.setup(setAdPlaying, setRemainingTime, isFirstPage, ppid);
    } catch (e) {
      captureSentryExceptionAndThrowLocal(e);
      this.preventAds = true;
    }
  }

  requestAds(): void {
    if (this.preventAds || !this.videoAds) {
      throw new VideoAdsException('Ads not initialized');
    }
    if (!this.shouldPlayAd()) {
      throw new VideoAdsException("Don't play ad: Still credits available");
    }

    this.videoAds.requestAds();
  }

  private shouldPlayAd(): boolean {
    return this.shouldPlayAdFn();
  }

  dispose(): void {
    this.imaScriptLoader.unload();
    if (this.videoAds) {
      this.videoAds.dispose();
    }
    this.videoAds = null;
    VideoAdController.instance = null;
  }
}
